import { MutationType, TargetType } from '@tencent/slide-sdk-pb-model';
import { assert } from 'ts-essentials';
import { BaseMutation } from '../base/base-mutation';
import { Rule } from '../base/mutation-rules';
import type { WithExtensions } from '../../data';
import type { PresentationDataModel } from '../../model/presentation-data-model';
import type { MaybeError } from '../base/base-mutation';
import type {
    IValidRemoveDataExtensionMutationData,
    IValidSetDataExtensionMutationData,
} from '../base/valid-mutation-data';

export class SetDataExtensionMutation extends BaseMutation<PresentationDataModel, IValidSetDataExtensionMutationData> {
    public readonly type: MutationType = MutationType.SetDataExtension;

    public apply(presentation: PresentationDataModel) {
        const { targetId, key, value } = this.data;

        const model = targetId ? presentation.store.models.get(targetId) : presentation;
        assert(model, `model not found: ${targetId}`);

        model.store.patchData({
            extensions: {
                [key]: value,
            },
        });
    }

    public invert(
        presentation: PresentationDataModel,
    ): [IValidSetDataExtensionMutationData | IValidRemoveDataExtensionMutationData] {
        const { pageId, targetId, key } = this.data;

        const model = targetId ? presentation.store.models.get(targetId) : presentation;
        assert(model, `model not found: ${targetId}`);

        const { extensions } = model.store.data as WithExtensions<string>;
        const oldValue = extensions?.[key];
        if (oldValue === undefined) {
            return [
                {
                    type: MutationType.RemoveDataExtension,
                    pageId,
                    targetId,
                    key,
                },
            ];
        }

        return [
            {
                type: MutationType.SetDataExtension,
                pageId,
                targetId,
                key,
                value: oldValue,
            },
        ];
    }

    // eslint-disable-next-line complexity
    public check(presentation: PresentationDataModel): MaybeError {
        const { targetType, pageId, targetId } = this.data;
        if (targetType === TargetType.Presentation) {
            return Rule.every(
                Rule.extensions.should.notHasPageId(pageId),
                Rule.extensions.should.notHasTargetId(targetId),
            );
        }

        if (targetType === TargetType.Theme) {
            return Rule.every(
                Rule.extensions.should.notHasPageId(pageId),
                Rule.extensions.should.hasTargetId(targetId),
                targetId ? Rule.theme.should.existInPresentation(presentation, targetId) : false,
            );
        }

        if (targetType === TargetType.Page) {
            return Rule.every(
                Rule.extensions.should.hasPageId(pageId),
                Rule.extensions.should.hasTargetId(targetId),
                Rule.extensions.should.equalTargetIdAndPageId(targetId, pageId),
                pageId ? Rule.page.should.exist(presentation, pageId) : false,
            );
        }

        if (targetType === TargetType.Shape) {
            return Rule.every(
                Rule.extensions.should.hasPageId(pageId),
                Rule.extensions.should.hasTargetId(targetId),
                pageId ? Rule.page.should.exist(presentation, pageId) : false,
                targetId && pageId ? Rule.shape.should.existInPage(presentation, targetId, pageId) : false,
            );
        }
        return new Error('unknown target_type');
    }
}
